import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Rss, Radio, Sparkles, HelpCircle, Download, Loader2, Mic, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { CocoonMark } from "@/components/CocoonMark";
import { Footer } from "@/components/Footer";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const COVER = `${process.env.REACT_APP_BACKEND_URL}/api/derive/cover.jpg`;
const FEATURED_TEASERS = [
  { slug: "prismes-foi-doute", title: { fr: "Les prismes de la foi et du doute", en: "The prisms of faith and doubt", es: "Los prismas de la fe y la duda", nl: "De prisma's van geloof en twijfel" } },
  { slug: "architecture-silence", title: { fr: "L'architecture du silence", en: "The architecture of silence", es: "La arquitectura del silencio", nl: "De architectuur van de stilte" } },
  { slug: "grand-sommeil-esprit", title: { fr: "Le grand sommeil de l'esprit", en: "The great sleep of the mind", es: "El gran sueño de la mente", nl: "De grote slaap van de geest" } },
  { slug: "exercice-souverainete", title: { fr: "L'exercice de la souveraineté", en: "The exercise of sovereignty", es: "El ejercicio de la soberanía", nl: "De uitoefening van soevereiniteit" } },
  { slug: "salaire-exister", title: { fr: "Le salaire d'exister", en: "The wage of existing", es: "El salario de existir", nl: "Het loon van het bestaan" } },
  { slug: "grand-oubli-vivant", title: { fr: "Le grand oubli du vivant", en: "The great forgetting of the living world", es: "El gran olvido de lo vivo", nl: "Het grote vergeten van het levende" } },
  { slug: "art-immobilite", title: { fr: "L'art de l'immobilité", en: "The art of stillness", es: "El arte de la quietud", nl: "De kunst van de stilte" } },
  { slug: "icone-iconoclaste", title: { fr: "L'icône de l'iconoclaste", en: "The icon of the iconoclast", es: "El icono del iconoclasta", nl: "Het icoon van de beeldenstormer" } },
  { slug: "transmettre-tempete", title: { fr: "Transmettre au milieu de la tempête", en: "Passing it on in the storm", es: "Transmitir en medio de la tormenta", nl: "Doorgeven midden in de storm" } },
  { slug: "tyrannie-du-conteneur", title: { fr: "La tyrannie du conteneur", en: "The tyranny of the container", es: "La tiranía del contenedor", nl: "De tirannie van de container" } },
  { slug: "piege-de-la-facilite", title: { fr: "Le piège de la facilité", en: "The trap of ease", es: "La trampa de la facilidad", nl: "De valkuil van het gemak" } },
];

const T = {
  fr: { title: "La Dérive", subtitle: "La newsletter audio hebdomadaire de COCOON — un fait, une question, une épiphanie.",
    rss: "S'abonner (RSS)", listen: "Écouter", didyou: "Le saviez-vous ?", question: "La question", empty: "Le premier épisode arrive bientôt…", back: "← Retour", eyebrow: "podcast · chaque semaine", listenOn: "Bientôt sur", soon: "bientôt", wa: "Envoyer sur WhatsApp", download: "Télécharger", preparing: "Création de l'image…", guest: "Proposer un sujet / thème", guestHint: "Une idée, une question, un thème que tu aimerais voir explorer dans La Dérive ? Propose-le.", guestName: "Ton nom", guestEmail: "Ton email", guestTopicPh: "Le sujet que tu aimerais explorer…", guestSend: "Envoyer ma candidature", guestSending: "Envoi…", guestThanks: "Merci ! Ta candidature est envoyée — vérifie ta boîte mail 🎙️", guestErr: "Oups, réessaie dans un instant.", guestCancel: "Annuler", teaserTitle: "Partage un teaser", teaserShare: "Partager" },
  en: { title: "The Drift", subtitle: "COCOON's weekly audio newsletter — a fact, a question, an epiphany.",
    rss: "Subscribe (RSS)", listen: "Listen", didyou: "Did you know?", question: "The question", empty: "The first episode is coming soon…", back: "← Back", eyebrow: "podcast · every week", listenOn: "Coming soon on", soon: "soon", wa: "Send on WhatsApp", download: "Download", preparing: "Creating the image…", guest: "Suggest a topic / theme", guestHint: "An idea, a question, a theme you'd love The Drift to explore? Suggest it.", guestName: "Your name", guestEmail: "Your email", guestTopicPh: "The topic you'd like to explore…", guestSend: "Send my application", guestSending: "Sending…", guestThanks: "Thanks! Your application is sent — check your inbox 🎙️", guestErr: "Oops, try again in a moment.", guestCancel: "Cancel", teaserTitle: "Share a teaser", teaserShare: "Share" },
  es: { title: "La Deriva", subtitle: "El boletín de audio semanal de COCOON — un dato, una pregunta, una epifanía.",
    rss: "Suscribirse (RSS)", listen: "Escuchar", didyou: "¿Sabías que?", question: "La pregunta", empty: "El primer episodio llega pronto…", back: "← Volver", eyebrow: "podcast · cada semana", listenOn: "Pronto en", soon: "pronto", wa: "Enviar por WhatsApp", download: "Descargar", preparing: "Creando la imagen…", guest: "Proponer un tema", guestHint: "¿Una idea, una pregunta, un tema que te gustaría que La Deriva explore? Propónlo.", guestName: "Tu nombre", guestEmail: "Tu email", guestTopicPh: "El tema que te gustaría explorar…", guestSend: "Enviar mi candidatura", guestSending: "Enviando…", guestThanks: "¡Gracias! Tu candidatura se ha enviado — revisa tu correo 🎙️", guestErr: "Vaya, inténtalo de nuevo en un momento.", guestCancel: "Cancelar", teaserTitle: "Comparte un teaser", teaserShare: "Compartir" },
  nl: { title: "De Drift", subtitle: "COCOON's wekelijkse audio-nieuwsbrief — een feit, een vraag, een epifanie.",
    rss: "Abonneren (RSS)", listen: "Beluisteren", didyou: "Wist je dat?", question: "De vraag", empty: "De eerste aflevering komt binnenkort…", back: "← Terug", eyebrow: "podcast · elke week", listenOn: "Binnenkort op", soon: "binnenkort", wa: "Sturen via WhatsApp", download: "Downloaden", preparing: "Afbeelding maken…", guest: "Stel een onderwerp voor", guestHint: "Een idee, een vraag of thema dat De Drift mag verkennen? Stel het voor.", guestName: "Je naam", guestEmail: "Je e-mail", guestTopicPh: "Het onderwerp dat je wilt verkennen…", guestSend: "Verstuur mijn aanmelding", guestSending: "Versturen…", guestThanks: "Bedankt! Je aanmelding is verstuurd — check je inbox 🎙️", guestErr: "Oeps, probeer het zo opnieuw.", guestCancel: "Annuleren", teaserTitle: "Deel een teaser", teaserShare: "Delen" },
};

// url: null → bouton désactivé « bientôt ». Renseigner l'URL pour activer le lien.
const PLATFORMS = [
  { id: "spotify", label: "Spotify", url: null },
  { id: "apple", label: "Apple Podcasts", url: null },
  { id: "youtube", label: "YouTube Music", url: null },
];

const PLATFORM_ICON = {
  spotify: (<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 2a10 10 0 100 20 10 10 0 000-20zm4.6 14.4a.62.62 0 01-.86.21c-2.35-1.44-5.3-1.76-8.79-.96a.62.62 0 11-.28-1.22c3.8-.87 7.08-.5 9.72 1.11.3.18.39.57.21.86zm1.23-2.74a.78.78 0 01-1.07.26c-2.69-1.65-6.79-2.13-9.97-1.17a.78.78 0 11-.45-1.49c3.63-1.1 8.15-.56 11.23 1.33.37.22.49.7.26 1.07zm.11-2.85C14.83 8.94 9.4 8.76 6.3 9.7a.93.93 0 11-.54-1.79c3.56-1.08 9.56-.87 13.32 1.36a.93.93 0 11-.95 1.6z"/></svg>),
  apple: (<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 2a10 10 0 00-3.16 19.49c-.05-.8-.02-1.77.18-2.64l1.14-4.83s-.29-.58-.29-1.43c0-1.34.78-2.34 1.75-2.34.82 0 1.22.62 1.22 1.36 0 .83-.53 2.07-.8 3.22-.23.96.48 1.75 1.43 1.75 1.71 0 2.87-2.2 2.87-4.8 0-1.98-1.33-3.46-3.76-3.46-2.74 0-4.45 2.04-4.45 4.33 0 .79.23 1.34.6 1.77.17.2.19.28.13.51l-.19.73c-.06.24-.25.32-.47.23-1.3-.53-1.91-1.96-1.91-3.56 0-2.65 2.23-5.82 6.66-5.82 3.56 0 5.9 2.58 5.9 5.35 0 3.66-2.03 6.39-5.03 6.39-1.01 0-1.95-.55-2.28-1.16l-.62 2.46c-.22.86-.66 1.72-1.06 2.4A10 10 0 1012 2z"/></svg>),
  youtube: (<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 2a10 10 0 100 20 10 10 0 000-20zm-2 14V8l6 4-6 4z"/></svg>),
};

const WaIcon = ({ size = 15 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor"><path d="M12.04 2c-5.46 0-9.9 4.44-9.9 9.9 0 1.75.46 3.45 1.32 4.95L2 22l5.28-1.38a9.87 9.87 0 004.76 1.21h.004c5.46 0 9.9-4.44 9.9-9.9 0-2.64-1.03-5.13-2.9-7A9.82 9.82 0 0012.04 2zm5.8 14.06c-.24.68-1.42 1.32-1.95 1.36-.5.05-1.12.24-3.66-.77-3.08-1.22-5.03-4.38-5.18-4.58-.15-.2-1.24-1.65-1.24-3.15s.79-2.24 1.07-2.55c.28-.3.6-.38.8-.38.2 0 .4.002.57.01.18.008.43-.07.67.51.24.6.82 2.07.9 2.22.07.15.12.32.02.52-.1.2-.15.32-.3.5-.15.17-.31.38-.44.51-.15.15-.3.31-.13.6.17.3.76 1.25 1.63 2.02 1.12 1 2.06 1.31 2.36 1.46.3.15.47.12.64-.07.17-.2.74-.86.94-1.16.2-.3.4-.25.67-.15.28.1 1.75.83 2.05.98.3.15.5.22.57.35.07.13.07.72-.17 1.4z"/></svg>
);

export default function Derive() {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const L = T[lang] || T.fr;
  const [episodes, setEpisodes] = useState(null);
  const [waBusy, setWaBusy] = useState(null);
  const [guestOpen, setGuestOpen] = useState(false);
  const [guestSent, setGuestSent] = useState(false);
  const [guestBusy, setGuestBusy] = useState(false);
  const [gName, setGName] = useState("");
  const [gEmail, setGEmail] = useState("");
  const [gTopic, setGTopic] = useState("");
  const [teaserBusy, setTeaserBusy] = useState(null);

  const submitGuest = async (e) => {
    e.preventDefault();
    if (guestBusy) return;
    if (!gEmail.trim() || gTopic.trim().length < 3) { toast.error(L.guestErr); return; }
    setGuestBusy(true);
    try {
      await api.post(`/derive/guest`, { name: gName.trim(), email: gEmail.trim(), topic: gTopic.trim(), lang });
      setGuestSent(true);
      toast.success(L.guestThanks);
    } catch {
      toast.error(L.guestErr);
    } finally {
      setGuestBusy(false);
    }
  };

  const shareTeaser = async (t) => {
    if (teaserBusy) return;
    setTeaserBusy(t.slug);
    const tid = toast.loading(L.preparing);
    const url = `${BACKEND}/derive/teasers/${t.slug}-${lang}.mp4`;
    try {
      const res = await fetch(url, { cache: "no-store" });
      if (!res.ok) throw new Error("teaser");
      const blob = await res.blob();
      const file = new File([blob], `la-derive-${t.slug}.mp4`, { type: "video/mp4" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        toast.dismiss(tid);
        try { await navigator.share({ files: [file], title: `${t.title[lang] || t.title.fr} — La Dérive · COCOON` }); } catch { /* cancelled */ }
        return;
      }
      const o = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = o; a.download = `la-derive-${t.slug}.mp4`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(o);
      toast.dismiss(tid);
    } catch {
      toast.error(L.guestErr, { id: tid });
    } finally {
      setTeaserBusy(null);
    }
  };

  const shareWhatsApp = async (ep) => {
    if (waBusy) return;
    const caption = `🎧 ${ep.subject} — La Dérive · COCOON\n${ep.audio_url}`;
    if (navigator.canShare) {
      setWaBusy(ep.week_key);
      const tid = toast.loading(L.preparing);
      try {
        const params = new URLSearchParams({ title: ep.subject || "", question: ep.question || "", label: ep.theme || ep.week_key || "", lang, url: "cocoonmind.com/derive" });
        const res = await fetch(`${BACKEND}/api/derive/story?${params}`);
        if (!res.ok) throw new Error("img");
        const blob = await res.blob();
        const file = new File([blob], `la-derive-${ep.week_key}.png`, { type: "image/png" });
        if (navigator.canShare({ files: [file] })) {
          toast.dismiss(tid);
          try { await navigator.share({ files: [file], text: caption, title: ep.subject }); } catch { /* cancelled */ }
          return;
        }
        toast.dismiss(tid);
      } catch { toast.dismiss(tid); }
      finally { setWaBusy(null); }
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(caption)}`, "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    api.get(`/derive/episodes?lang=${lang}`).then(({ data }) => setEpisodes(data)).catch(() => setEpisodes([]));
  }, [lang]);

  const rssUrl = `${BACKEND}/api/derive/rss.xml?lang=${lang}`;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="fixed top-0 inset-x-0 z-50 glass">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-display text-2xl tracking-tight"><CocoonMark withMind /></Link>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <Link to="/" className="text-sm text-foreground/60 hover:text-primary transition-colors">{L.back}</Link>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-6 pt-32 pb-24">
        <div className="text-center mb-12">
          <img src={COVER} alt="La Dérive — podcast" data-testid="derive-cover"
            className="w-40 h-40 sm:w-48 sm:h-48 rounded-2xl mx-auto mb-6 border border-primary/20 shadow-2xl object-cover" />
          <p className="uppercase tracking-[0.24em] text-xs text-primary/70 mb-3">{L.eyebrow}</p>
          <h1 className="font-display text-gradient-orange text-4xl sm:text-5xl tracking-tight mb-3" data-testid="derive-title">{L.title}</h1>
          <p className="text-foreground/60 max-w-xl mx-auto leading-relaxed">{L.subtitle}</p>
          <a href={rssUrl} target="_blank" rel="noreferrer" data-testid="derive-rss-link"
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-primary/30 text-primary hover:bg-primary/10 transition-colors text-sm font-semibold">
            <Rss size={15} /> {L.rss}
          </a>

          <div className="mt-8" data-testid="derive-platforms">
            <p className="uppercase tracking-[0.2em] text-[0.65rem] text-foreground/35 mb-3">{L.listenOn}</p>
            <div className="flex flex-wrap justify-center gap-3">
              {PLATFORMS.map((p) => {
                const inner = (
                  <>
                    <span className="text-primary flex-shrink-0">{PLATFORM_ICON[p.id]}</span>
                    <span className="text-foreground/85">{p.label}</span>
                    {!p.url && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-primary/15 text-primary/80">{L.soon}</span>}
                  </>
                );
                const cls = "glass rounded-full border border-primary/12 pl-3 pr-4 py-2 flex items-center gap-2 text-sm";
                return p.url ? (
                  <a key={p.id} href={p.url} target="_blank" rel="noreferrer" data-testid={`derive-platform-${p.id}`}
                    className={`${cls} hover:border-primary/40 hover:-translate-y-0.5 transition-all`}>{inner}</a>
                ) : (
                  <span key={p.id} data-testid={`derive-platform-${p.id}`} title={L.soon}
                    className={`${cls} opacity-70 cursor-not-allowed`}>{inner}</span>
                );
              })}
            </div>
          </div>

          <div className="mt-8 flex flex-col items-center gap-3" data-testid="derive-guest-cta">
            {!guestOpen && !guestSent && (
              <>
                <button
                  onClick={() => setGuestOpen(true)}
                  data-testid="derive-guest-button"
                  className="inline-flex items-center gap-2.5 rounded-full bg-primary text-background font-semibold pl-5 pr-6 py-3 text-sm hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(236,198,128,0.3)] transition-all"
                >
                  <Mic size={16} strokeWidth={2} /> {L.guest}
                </button>
                <p className="text-foreground/40 text-xs max-w-xs text-center leading-relaxed">{L.guestHint}</p>
              </>
            )}

            {guestOpen && !guestSent && (
              <form onSubmit={submitGuest} data-testid="derive-guest-form"
                className="w-full max-w-md rounded-2xl border border-primary/20 bg-white/[0.03] p-5 flex flex-col gap-3 text-left">
                <div className="flex items-center gap-2 text-primary font-semibold text-sm"><Mic size={15} /> {L.guest}</div>
                <input value={gName} onChange={(e) => setGName(e.target.value)} placeholder={L.guestName}
                  data-testid="guest-name-input" maxLength={80}
                  className="rounded-xl bg-background/60 border border-white/10 px-4 py-2.5 text-sm text-foreground placeholder:text-foreground/35 focus:border-primary/50 focus:outline-none" />
                <input value={gEmail} onChange={(e) => setGEmail(e.target.value)} placeholder={L.guestEmail} type="email" required
                  data-testid="guest-email-input"
                  className="rounded-xl bg-background/60 border border-white/10 px-4 py-2.5 text-sm text-foreground placeholder:text-foreground/35 focus:border-primary/50 focus:outline-none" />
                <textarea value={gTopic} onChange={(e) => setGTopic(e.target.value)} placeholder={L.guestTopicPh} rows={3} required
                  data-testid="guest-topic-input" maxLength={1000}
                  className="rounded-xl bg-background/60 border border-white/10 px-4 py-2.5 text-sm text-foreground placeholder:text-foreground/35 focus:border-primary/50 focus:outline-none resize-none" />
                <div className="flex items-center gap-2">
                  <button type="submit" disabled={guestBusy} data-testid="guest-submit-button"
                    className="inline-flex items-center gap-2 rounded-full bg-primary text-background font-semibold px-5 py-2.5 text-sm disabled:opacity-50 hover:-translate-y-0.5 transition-all">
                    {guestBusy ? <Loader2 size={15} className="animate-spin" /> : <Mic size={15} />} {guestBusy ? L.guestSending : L.guestSend}
                  </button>
                  <button type="button" onClick={() => setGuestOpen(false)} data-testid="guest-cancel-button"
                    className="text-foreground/50 text-sm px-3 py-2 hover:text-foreground/80 transition-colors">{L.guestCancel}</button>
                </div>
              </form>
            )}

            {guestSent && (
              <div data-testid="derive-guest-success"
                className="w-full max-w-md rounded-2xl border border-primary/30 bg-primary/10 px-5 py-4 text-center text-sm text-foreground/90">
                {L.guestThanks}
              </div>
            )}
          </div>

          <div className="mt-10 pt-6 border-t border-white/10 flex flex-col items-center gap-3" data-testid="derive-teaser-share">
            <p className="text-foreground/50 text-xs uppercase tracking-[0.2em]">{L.teaserTitle}</p>
            <div className="flex flex-wrap items-center justify-center gap-2.5">
              {FEATURED_TEASERS.map((t) => (
                <button key={t.slug} onClick={() => shareTeaser(t)} disabled={teaserBusy === t.slug}
                  data-testid={`share-teaser-${t.slug}`}
                  className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-white/[0.03] pl-3.5 pr-4 py-2 text-xs text-foreground/85 hover:bg-primary/15 hover:border-primary/50 hover:text-primary transition-all disabled:opacity-50">
                  {teaserBusy === t.slug ? <Loader2 size={13} className="animate-spin" /> : <Share2 size={13} />} {t.title[lang] || t.title.fr}
                </button>
              ))}
            </div>
          </div>
        </div>

        {episodes === null ? (
          <div className="text-center text-foreground/40 py-16 font-display italic text-xl">…</div>
        ) : episodes.length === 0 ? (
          <div className="text-center text-foreground/45 py-16 font-display italic text-xl" data-testid="derive-empty">{L.empty}</div>
        ) : (
          <div className="space-y-6" data-testid="derive-episodes">
            {episodes.map((ep) => {
              const mm = Math.max(1, Math.round((ep.audio_seconds || 0) / 60));
              return (
                <article key={ep.week_key} data-testid={`derive-episode-${ep.week_key}`}
                  className="glass rounded-2xl border border-white/10 p-6 hover:border-primary/25 transition-colors">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-[0.7rem] uppercase tracking-wider text-primary/60">{ep.week_key}</span>
                    {ep.theme && <span className="text-[0.7rem] px-2 py-0.5 rounded-full bg-primary/10 text-primary/80">{ep.theme}</span>}
                    <span className="text-[0.7rem] text-foreground/40 ml-auto">≈ {mm} min</span>
                  </div>
                  <h2 className="font-display italic text-2xl text-foreground mb-4">{ep.subject}</h2>

                  <audio controls preload="none" src={ep.audio_url} data-testid={`derive-audio-${ep.week_key}`} className="w-full mb-4" />

                  <div className="flex flex-wrap items-center gap-2.5 mb-5">
                    <button
                      onClick={() => shareWhatsApp(ep)}
                      disabled={waBusy === ep.week_key}
                      data-testid={`derive-wa-${ep.week_key}`}
                      className="inline-flex items-center gap-2 text-sm text-[#25D366] hover:text-[#25D366] border border-[#25D366]/30 hover:border-[#25D366]/60 bg-[#25D366]/5 rounded-full px-4 py-2 transition-colors disabled:opacity-50"
                    >
                      {waBusy === ep.week_key ? <Loader2 size={14} className="animate-spin" /> : <WaIcon />} {L.wa}
                    </button>
                    <a
                      href={ep.audio_url} download
                      data-testid={`derive-dl-${ep.week_key}`}
                      className="inline-flex items-center gap-2 text-sm text-foreground/60 hover:text-primary border border-white/10 hover:border-primary/40 rounded-full px-4 py-2 transition-colors"
                    >
                      <Download size={14} /> {L.download}
                    </a>
                  </div>

                  {ep.fact && (
                    <div className="mb-3">
                      <div className="flex items-center gap-2 text-primary/70 text-[0.7rem] uppercase tracking-wider mb-1"><Sparkles size={12} /> {L.didyou}</div>
                      <p className="text-foreground/75 leading-relaxed text-sm">{ep.fact}</p>
                    </div>
                  )}
                  {ep.question && (
                    <div>
                      <div className="flex items-center gap-2 text-primary/70 text-[0.7rem] uppercase tracking-wider mb-1"><HelpCircle size={12} /> {L.question}</div>
                      <p className="font-display italic text-foreground/85 leading-relaxed">{ep.question}</p>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
